import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { BoxProps, SxProps, Theme } from "@mui/material";

/**
 * Reusable layouts
 * ==================
 * The shared, constrained set of building blocks that pages COMPOSE instead of
 * hand-authoring `styled.div` layout at the top of every file.
 *
 * The rule:
 *   - How a COMPONENT looks  -> MUI theme (createTheme) + MUI components.
 *   - How a PAGE is arranged -> these layouts, composed via props.
 *   - Never author raw layout CSS (display:grid; gap; @media...) per page.
 *
 * Everything here is built on MUI's Box, so the responsive object syntax
 * ({ xs, sm, md, lg, xl }) reads breakpoints from the MUI theme. That keeps
 * one source of truth for breakpoints instead of a parallel media-query helper.
 *
 * Every token named here is a @outpave/ds token: the package paints the
 * --space-N and --color-* variables into :root, so nothing local survives it.
 */

/** A value that can be set once, or per breakpoint. */
type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl";
type Responsive<T> = T | Partial<Record<Breakpoint, T>>;

/** Default gap between items. The semantic "one rhythm unit" of the layout. */
const GAP = "var(--space-5)";

/**
 * Every layout primitive sets `color: "inherit"` in its default sx so the text
 * colour cascades through it into descendants. CSS already inherits colour by
 * default, but MUI Box's generated styling can interpose enough to interrupt
 * the cascade when these primitives are nested inside a coloured surface
 * (e.g. `<Panel variant="brand">`). Setting it explicitly keeps the chain
 * reliable across all use sites.
 */

/**
 * Merge a layout's base styles with a caller's `sx`, letting the caller win.
 * Uses MUI's array form so an `sx` that is an object, a theme callback, or an
 * array all compose correctly.
 */
const mergeSx = (base: SxProps<Theme>, sx?: SxProps<Theme>): SxProps<Theme> =>
  [base, ...(Array.isArray(sx) ? sx : [sx])].filter(Boolean) as SxProps<Theme>;

// minmax(0, 1fr) instead of a bare 1fr: stops a wide child (long text, a chart)
// from blowing the track out past its share of the row.
const toTemplate = (value: number | string): string =>
  typeof value === "number" ? `repeat(${value}, minmax(0, 1fr))` : value;

const resolveTemplate = (
  count: Responsive<number | string>,
): Responsive<string> => {
  if (typeof count === "object") {
    const out: Partial<Record<Breakpoint, string>> = {};
    (Object.keys(count) as Breakpoint[]).forEach((bp) => {
      const value = count[bp];
      if (value !== undefined) out[bp] = toTemplate(value);
    });
    return out;
  }
  return toTemplate(count);
};

/**
 * Page
 * ----
 * The outer shell every page shares: caps width, centres horizontally, applies
 * page padding + background, and lays its sections out as a vertical rhythm
 * with a consistent gap. This is the thing that replaces the per-page
 * `const Page = styled.div`.
 *
 * Most pages just drop their sections inside and let them flow. A page that
 * needs to fill the viewport at md+ so a chart or list row grows toward the
 * bottom of the screen (e.g. the platform dashboard) passes a responsive
 * `height` and `gridTemplateRows` through `sx` (only at md+). At sub-md the
 * shell falls back to natural flow so a phone-shaped viewport never crushes
 * `1fr` rows and never causes rows to visually overlap. Rows that should
 * stretch but stay bounded carry their own minHeight/maxHeight in sx.
 */
export const Page = styled(Box)`
  /* The shell's content column is a flex container, so without a width the auto margins shrink this to its content. */
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  min-height: 100%;
  display: grid;
  grid-auto-rows: min-content;
  gap: ${GAP};
  padding: 0 var(--space-7) var(--space-7);
  background-color: var(--color-bg-page);
  color: inherit;
`;

interface ColumnsProps extends Omit<BoxProps, "columns"> {
  /**
   * Number of equal columns, or a raw grid-template-columns string for an
   * asymmetric split (e.g. "6fr 4fr"). Pass a responsive object to change the
   * count per breakpoint, mobile-first:
   *   count={{ xs: 1, sm: 2, lg: 5 }}   // 1 col on phones, 2 on tablets, 5 on desktop
   *   count="6fr 4fr"                   // fixed asymmetric split
   */
  count?: Responsive<number | string>;
  /** Gap between cells. Defaults to the --space-5 token. */
  gap?: string;
}

/**
 * Columns
 * -------
 * A responsive CSS-grid row. The workhorse: it replaces BannerRow,
 * StatCardRow, ChartRow, TopListsRow and every other one-off grid `styled.div`.
 * Compose the layout with props instead of authoring `display: grid` per page.
 */
// Note: none of these layouts force `minHeight: 0`. Setting it as a default
// breaks parent grids that rely on `minmax(min-content, 1fr)` row sizing:
// every layout becomes effectively `minHeight: 0`, the parent's min-content
// resolves to 0, the `1fr` rows can collapse on short viewports, and the
// children visually overflow into the next row. If a specific use case needs
// a layout to shrink below its content (e.g. a nested scroller), pass
// `sx={{ minHeight: 0 }}` at the call site.
export const Columns: React.FC<ColumnsProps> = ({
  count = 1,
  gap = GAP,
  sx,
  children,
  ...rest
}) => (
  <Box
    sx={mergeSx(
      {
        display: "grid",
        gridTemplateColumns: resolveTemplate(count),
        gap,
        color: "inherit",
      },
      sx,
    )}
    {...rest}
  >
    {children}
  </Box>
);

interface RowsProps extends Omit<BoxProps, "rows"> {
  /**
   * Number of equal rows, or a raw grid-template-rows string for an asymmetric
   * split (e.g. "auto 1fr"). Pass a responsive object to change the count per
   * breakpoint, mobile-first:
   *   count={2}                  // two equal rows
   *   count={{ xs: 1, md: 2 }}   // 1 row on phones, 2 on tablet+
   *   count="auto 1fr"           // header + content
   */
  count?: Responsive<number | string>;
  /** Gap between cells. Defaults to the --space-5 token. */
  gap?: string;
}

/**
 * Rows
 * ----
 * The vertical counterpart to Columns: a CSS-grid column whose tracks are
 * equal-sized rows by default. Reach for this (instead of Stack) when you need
 * the children to split available height evenly, e.g. a column-of-cards next
 * to a tall chart where each card should fill its share. Stack flows by
 * natural height; Rows enforces equal-height tracks.
 */
export const Rows: React.FC<RowsProps> = ({
  count = 1,
  gap = GAP,
  sx,
  children,
  ...rest
}) => (
  <Box
    sx={mergeSx(
      {
        display: "grid",
        gridTemplateRows: resolveTemplate(count),
        gap,
        color: "inherit",
      },
      sx,
    )}
    {...rest}
  >
    {children}
  </Box>
);

interface StackProps extends BoxProps {
  /** Gap between stacked items. Defaults to the --space-5 token. */
  gap?: string;
}

/**
 * Stack
 * -----
 * Vertical flow: children stack top to bottom with a token gap, each taking
 * its natural height. This is the default for "a column of things" (a sidebar,
 * a form, a card's contents). When you need equal-height rows that divide the
 * available space, use <Rows count={N}> instead.
 *
 * (MUI also ships <Stack>; this is the token-aware default so spacing values
 *  are never repeated. Reach for MUI's directly if you need its `divider` or
 *  numeric `spacing` API.)
 */
export const Stack: React.FC<StackProps> = ({
  gap = GAP,
  sx,
  children,
  ...rest
}) => (
  <Box
    sx={mergeSx(
      { display: "flex", flexDirection: "column", gap, color: "inherit" },
      sx,
    )}
    {...rest}
  >
    {children}
  </Box>
);

interface InlineProps extends BoxProps {
  /** Gap between items. Defaults to the --space-5 token. */
  gap?: string;
  /** Cross-axis alignment. Defaults to "center". */
  align?: "start" | "center" | "end" | "stretch" | "baseline";
  /** Main-axis distribution. Defaults to "start". */
  justify?: "start" | "center" | "end" | "between" | "around";
  /** Wrap to multiple lines when space runs out. Defaults to true. */
  wrap?: boolean;
}

const FLEX_ALIGN = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  stretch: "stretch",
  baseline: "baseline",
} as const;

const FLEX_JUSTIFY = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  between: "space-between",
  around: "space-around",
} as const;

/**
 * Inline
 * ------
 * Horizontal cluster: children flow left to right with a token gap and wrap
 * onto new lines by default. The right layout for toolbars, button groups,
 * chip rows, and "icon + label" pairs. Likely to show up on most pages.
 */
export const Inline: React.FC<InlineProps> = ({
  gap = GAP,
  align = "center",
  justify = "start",
  wrap = true,
  sx,
  children,
  ...rest
}) => (
  <Box
    sx={mergeSx(
      {
        display: "flex",
        flexDirection: "row",
        flexWrap: wrap ? "wrap" : "nowrap",
        alignItems: FLEX_ALIGN[align],
        justifyContent: FLEX_JUSTIFY[justify],
        gap,
        color: "inherit",
      },
      sx,
    )}
    {...rest}
  >
    {children}
  </Box>
);

interface AutoGridProps extends BoxProps {
  /** Minimum width of each cell before the grid drops to fewer columns. */
  min?: string;
  /** Gap between cells. Defaults to the --space-5 token. */
  gap?: string;
}

/**
 * AutoGrid
 * --------
 * A responsive grid with NO breakpoints to maintain. Cells are at least `min`
 * wide and the column count adapts to the available width automatically. Ideal
 * for card galleries / tile lists where the exact column count does not matter.
 *   <AutoGrid min="240px">{cards}</AutoGrid>
 */
export const AutoGrid: React.FC<AutoGridProps> = ({
  min = "240px",
  gap = GAP,
  sx,
  children,
  ...rest
}) => (
  <Box
    sx={mergeSx(
      {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}, 100%), 1fr))`,
        gap,
        color: "inherit",
      },
      sx,
    )}
    {...rest}
  >
    {children}
  </Box>
);

interface CenterProps extends BoxProps {
  /** Optional cap on the content width while it stays centred. */
  maxWidth?: number | string;
}

/**
 * Center
 * ------
 * Horizontally centres its content and optionally caps the content width.
 * Handy for a narrow form, an empty state, or a confirmation panel sitting on
 * an otherwise wide page.
 */
export const Center: React.FC<CenterProps> = ({
  maxWidth,
  sx,
  children,
  ...rest
}) => (
  <Box
    sx={mergeSx(
      {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        color: "inherit",
        "& > *": { width: "100%", maxWidth },
      },
      sx,
    )}
    {...rest}
  >
    {children}
  </Box>
);
